import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateUserBodyInput,
  RemoveUserImageBodyInput,
  SwitchMarketBodyInput,
  UpdateClientRelationsBodyInput,
  UpdateMeBodyInput,
  UpdateUserDetailsBodyInput,
  UpdateUserPermissionsBodyInput,
} from '@utilities-one/reipm-api-sdk';

import { getClientApiUrl } from '@/config/getEnv';
import { type UserDetails, userQueryKeys } from '@/entities/user/query/user.queries';
import { firstFieldError } from '@/library/api/firstFieldError';
import { api, parseResponse } from '@/library/api/honoClient';
import { redirectToLoginOn401 } from '@/library/api/redirectToLoginOn401';

/**
 * UG-4500 — user entity write layer (replaces `entities/user/actions/*`
 * server actions). Every mutation invalidates `userQueryKeys.all` on success
 * so grids, detail views, and pickers refetch.
 *
 * `useUpdateUserImage` (UG-4500 review round) replaces the legacy
 * `updateImage` + `uploadImageToS3` pair: ONE multipart request — the API
 * uploads to S3 itself and persists the URL on `user.image`.
 */

/** Invalidate every users-derived query (grid pages, details, pickers). */
const useInvalidateUsers = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
};

export const useCreateUser = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (json: CreateUserBodyInput) => parseResponse(api.users.create.$post({ json })),
    onSuccess: invalidate,
  });
};

export const useUpdateUserDetails = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (json: UpdateUserDetailsBodyInput) => parseResponse(api.users.updateDetails.$post({ json })),
    onSuccess: invalidate,
  });
};

/**
 * `users/updatePermissions` (UG-5193) — stores the personal override bag (the
 * FULL effective snapshot gpm-ui's `toOverrideBag` builds) or `null` to
 * inherit the role again. `userQueryKeys.all` also prefixes the
 * `['users','me']` session query (`meQueryKey`), so editing one's own
 * overrides refreshes `useAccess()` without a reload.
 */
export const useUpdateUserPermissions = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (json: UpdateUserPermissionsBodyInput) => parseResponse(api.users.updatePermissions.$post({ json })),
    onSuccess: invalidate,
  });
};

/**
 * `users/updateMe` (UG-4770) — self-service name/email/password for the
 * signed-in user (authGuard only; the schema strips every privileged field).
 * The `userQueryKeys.all` invalidation also refetches the `['users','me']`
 * AccessProvider query, so the topbar name updates without a reload.
 */
export const useUpdateMe = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (json: UpdateMeBodyInput) => parseResponse(api.users.updateMe.$post({ json })),
    onSuccess: invalidate,
  });
};

/**
 * `users/updateImage` (multipart — no typed RPC input, so a plain fetch
 * mirrors the honoClient defaults: cookie credentials + 401 redirect, the
 * `useCreateQCResponse` pattern). The API allows updating one's OWN photo
 * always, anyone else's behind `users.update`. Returns the refreshed
 * `userDetailsWithRelations` (same shape as `getUserDetails`).
 */
export const useUpdateUserImage = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: async ({ userId, file }: { userId: string; file: File }): Promise<UserDetails> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', userId);

      const response = await fetch(`${getClientApiUrl()}/users/updateImage`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      }).then(redirectToLoginOn401);

      let body: unknown = null;
      try {
        body = await response.json();
      } catch {
        // ignore
      }

      if (!response.ok) {
        const message =
          firstFieldError(body) ??
          (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string'
            ? (body as { error: string }).error
            : null);
        throw new Error(message ?? `Failed to update user image (${response.status})`);
      }

      return body as UserDetails;
    },
    onSuccess: invalidate,
  });
};

/**
 * `users/removeImage` (UG-4756) — clears the photo and best-effort-deletes the
 * S3 object; returns the refreshed `userDetailsWithRelations` like
 * `updateDetails`/`updateImage`. Same guard as `updateImage`: own photo
 * always, anyone else's behind `users.update`.
 */
export const useRemoveUserImage = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (json: RemoveUserImageBodyInput) => parseResponse(api.users.removeImage.$post({ json })),
    onSuccess: invalidate,
  });
};

export const useUpdateClientRelations = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (json: UpdateClientRelationsBodyInput) =>
      parseResponse(api.users.updateClientRelations.$post({ json })),
    onSuccess: invalidate,
  });
};

/**
 * `users/deactivate` (UG-4850, replaces `users/remove`) — a status change, not
 * a soft delete: sets `status = inactive` and signs the user out everywhere;
 * the row stays in every grid with the Inactive badge.
 */
export const useDeactivateUser = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (json: { id: string }) => parseResponse(api.users.deactivate.$post({ json })),
    onSuccess: invalidate,
  });
};

/**
 * Sign in AS another user (ported legacy `impersonate` action). The API swaps
 * the httpOnly auth cookie to a fresh session for the target (stamped with
 * `impersonatedById`) — the caller MUST follow success with a full page
 * reload so every query re-resolves under the new identity.
 */
export const useImpersonateUser = () =>
  useMutation({
    mutationFn: (json: { userId: string }) => parseResponse(api.users.impersonate.$post({ json })),
  });

/** Return from an impersonated session to the original account (cookie swap back — full reload required too). */
export const useStopImpersonating = () =>
  useMutation({
    mutationFn: () => parseResponse(api.users.stopImpersonating.$post()),
  });

/**
 * `users/switchMarket` (UG-4847) — a multi-market Admin flips their active
 * market in place. The argument-less `invalidateQueries()` is deliberate
 * (ticket: "refresh the queries"): every cached query goes stale and the
 * active ones refetch under the new market scope, including AccessProvider's
 * `['users','me']` despite its staleTime; returning its promise keeps the
 * mutation pending until the refetches settle. Callers that follow success
 * with a full reload (the post-login picker) pass `invalidate: false`.
 */
export const useSwitchMarket = ({ invalidate = true } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (json: SwitchMarketBodyInput) => parseResponse(api.users.switchMarket.$post({ json })),
    onSuccess: () => (invalidate ? queryClient.invalidateQueries() : undefined),
  });
};

export const useCreateUserComment = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (json: { content: string; userId: string }) => parseResponse(api.users.createComment.$post({ json })),
    onSuccess: invalidate,
  });
};

/* ---------------------------------------------------------------------------
 * Skills are their own API entity, but every skill mutation returns the
 * updated USER details (legacy `actions.skills.*` behavior), so their write
 * layer lives with the user entity that owns the affected queries.
 * ------------------------------------------------------------------------- */

export const useAddUserSkill = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (json: { name: string; userId: string }) => parseResponse(api.skills.create.$post({ json })),
    onSuccess: invalidate,
  });
};

export const useRemoveUserSkill = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (json: { id: string; userId: string }) => parseResponse(api.skills.remove.$post({ json })),
    onSuccess: invalidate,
  });
};
